import _flatMapInstanceProperty from "@babel/runtime-corejs3/core-js/instance/flat-map";
import _reduceInstanceProperty from "@babel/runtime-corejs3/core-js/instance/reduce";
import { computed, ref } from "@vue/composition-api";
import { useVuelidate } from "@vuelidate/core";
import { between, maxValue } from "@vuelidate/validators";
import { minValueNotRequired } from "@Shared/@Utils/new-validation-rules";
import * as ApplicationStandaloneGetters from "@Client/@Modules/application-standalone/store/application-standalone-getters";
import { futureDate, withValidDate } from "@Shared/@Utils/validation-rules";
import { getValidationRulesFromMap } from "@Client/@Composables/application-standalone/validations/getValidationRulesFromMap";
import { useApplicationStandaloneStoreGetters } from "@Client/@Modules/application-standalone/store/application-standalone-helpers";
import { otherPropertiesExistingMortgageBindingsAndVisibilities } from "@Client/@Composables/application-standalone/pages/other-properties/otherPropertiesExistingMortgageBindingsAndVisibilities";
import { useApplicantHelpers } from "@Client/@Composables/application-standalone/pages/applicants/useApplicantHelpers";
import { camelCase, capitalize, mapKeys } from "lodash";
export const otherPropertyExistingMortgageValidator = (applicantIndex, otherPropertyIndex, existingMortgageIndex, {
  lazy = false
} = {}) => {
  const {
    bindings
  } = otherPropertiesExistingMortgageBindingsAndVisibilities(applicantIndex, otherPropertyIndex, existingMortgageIndex, ref("0"));
  const {
    existingMortgageBalanceRemaining,
    existingMortgageFrequency,
    existingMortgageInterestRate,
    existingMortgageLender,
    existingMortgageLoanType,
    existingMortgageMaturityDate,
    existingMortgageMortgageType,
    existingMortgageOrigAmount,
    existingMortgagePaymentAmount,
    existingMortgagePayoff,
    existingMortgageRateType,
    existingMortgageTermType
  } = bindings;
  const applicantPrefix = computed(() => {
    return applicantIndex.value ? "applicant" : "";
  });
  const {
    GET_SCHEMA_RULES_BY_PATH,
    HAS_CONSOLIDATED_REQUEST
  } = useApplicationStandaloneStoreGetters([ApplicationStandaloneGetters.GET_SCHEMA_RULES_BY_PATH, ApplicationStandaloneGetters.HAS_CONSOLIDATED_REQUEST]);
  const {
    isApplicantVisibleFromFillOut,
    getApplicantIdByIndex
  } = useApplicantHelpers();
  const areRulesVisibleFromFillOut = () => !HAS_CONSOLIDATED_REQUEST.value || isApplicantVisibleFromFillOut(getApplicantIdByIndex(applicantIndex.value));
  const rules = computed(() => {
    return getValidationRulesFromMap(areRulesVisibleFromFillOut, GET_SCHEMA_RULES_BY_PATH, otherPropertyExistingMortgagePageRules, applicantPrefix.value, {
      "co-applicants": applicantIndex.value - 1,
      "other-properties": otherPropertyIndex.value,
      "other-property-existing-mortgages": existingMortgageIndex.value
    });
  });
  const v$ = useVuelidate(rules, {
    existingMortgageBalanceRemaining,
    existingMortgageFrequency,
    existingMortgageInterestRate,
    existingMortgageLender,
    existingMortgageLoanType,
    existingMortgageMaturityDate,
    existingMortgageMortgageType,
    existingMortgageOrigAmount,
    existingMortgagePaymentAmount,
    existingMortgagePayoff,
    existingMortgageRateType,
    existingMortgageTermType
  }, {
    $scope: "otherProperty",
    $lazy: lazy
  });
  return {
    v$
  };
};
const otherPropertyExistingMortgagePageRules = {
  existingMortgageBalanceRemaining: {
    between: between(0, 1000000000000)
  },
  existingMortgageFrequency: {},
  existingMortgageInterestRate: {},
  existingMortgageLender: {},
  existingMortgageLoanType: {},
  existingMortgageMaturityDate: {
    withValidDate: () => withValidDate,
    futureDate: () => futureDate
  },
  existingMortgageMortgageType: {},
  existingMortgageOrigAmount: {
    minValue: required => minValueNotRequired(100, required),
    maxValue: maxValue(1000000000000)
  },
  existingMortgagePaymentAmount: {},
  existingMortgagePayoff: {},
  existingMortgageRateType: {},
  existingMortgageTermType: {}
};
export const otherPropertiesExistingMortgageValidator = ({
  lazy = false
} = {}) => {
  const {
    GET_SCHEMA_RULES_BY_PATH,
    HAS_CONSOLIDATED_REQUEST
  } = useApplicationStandaloneStoreGetters([ApplicationStandaloneGetters.GET_SCHEMA_RULES_BY_PATH, ApplicationStandaloneGetters.HAS_CONSOLIDATED_REQUEST]);
  const {
    allOtherPropertiesExistingMortgage: otherProperties
  } = useApplicantHelpers();
  const {
    isApplicantVisibleFromFillOut
  } = useApplicantHelpers();
  const areRulesVisibleFromFillOut = ({
    id,
    applicantId
  }) => !HAS_CONSOLIDATED_REQUEST.value || isApplicantVisibleFromFillOut(id || applicantId);
  const allOtherProperties = computed(() => {
    var _context;
    return _flatMapInstanceProperty(_context = otherProperties.value).call(_context, item => areRulesVisibleFromFillOut(item) ? mapKeys(item, (_value, key) => camelCase("existingMortgage" + capitalize(key))) : []);
  });
  const getRules = () => {
    var _context2;
    const mainApplicantRules = (otherPropertyIndex, index) => getValidationRulesFromMap(() => true, GET_SCHEMA_RULES_BY_PATH, otherPropertyExistingMortgagePageRules, "", {
      "other-properties": otherPropertyIndex,
      "other-property-existing-mortgages": index
    });
    const coApplicantRules = (applicantIndex, otherPropertyIndex, index) => getValidationRulesFromMap(() => true, GET_SCHEMA_RULES_BY_PATH, otherPropertyExistingMortgagePageRules, "applicant", {
      "co-applicants": applicantIndex,
      "other-properties": otherPropertyIndex,
      "other-property-existing-mortgages": index
    });
    return _reduceInstanceProperty(_context2 = allOtherProperties.value).call(_context2, (acc, otherProperty, index) => {
      acc[index] = otherProperty.applicantIndex ? coApplicantRules(otherProperty.index, otherProperty.otherPropertyIndex, otherProperty.otherPropertyExistingMortgageIndex) : mainApplicantRules;
      return acc;
    }, {});
  };
  const rules = computed(getRules);
  const v$ = useVuelidate(rules, allOtherProperties, {
    $scope: "otherPropertyExistingMortgage",
    $lazy: lazy
  });
  return {
    v$
  };
};